import { ref, readonly } from 'vue'

const userLat = ref(null)
const userLng = ref(null)
const locationReady = ref(false)
const locationSource = ref(null) // 'gps' | 'ip' | null
let initPromise = null

/**
 * Haversine distance in km between two lat/lng pairs.
 */
function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

/**
 * Try browser geolocation first, fall back to IP-based geolocation.
 */
async function initLocation() {
  if (initPromise) return initPromise

  initPromise = (async () => {
    // 1. Try browser Geolocation API
    try {
      const pos = await new Promise((resolve, reject) => {
        if (!navigator.geolocation) return reject(new Error('No geolocation'))
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 5000,
          maximumAge: 1000 * 60 * 30, // cache for 30 min
        })
      })
      userLat.value = pos.coords.latitude
      userLng.value = pos.coords.longitude
      locationSource.value = 'gps'
      locationReady.value = true
      return
    } catch {
      // User denied or unavailable — fall through to IP
    }

    // 2. IP-based fallback (ip-api.com — free, no key, returns lat/lon)
    try {
      const res = await fetch('http://ip-api.com/json/?fields=lat,lon,status')
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      if (data.status === 'success') {
        userLat.value = data.lat
        userLng.value = data.lon
        locationSource.value = 'ip'
        locationReady.value = true
        return
      }
    } catch {
      // IP geolocation also failed
    }

    // 3. Both failed
    locationReady.value = false
  })()

  return initPromise
}

/**
 * Get human-readable distance string between user and an equipment item.
 * @param {Object} item - Equipment object with `address.latitude` & `address.longitude`
 * @returns {string|null} e.g. "1.5 mi" or "750 mi" or null
 */
function getDistance(item) {
  if (!locationReady.value || !userLat.value || !userLng.value) return null
  const lat = parseFloat(item?.address?.latitude)
  const lng = parseFloat(item?.address?.longitude)
  if (isNaN(lat) || isNaN(lng)) return null
  const km = haversineKm(userLat.value, userLng.value, lat, lng)
  const mi = km * 0.621371
  if (mi < 0.1) return `${Math.round(mi * 5280)} ft away`
  if (mi < 100) return `${mi.toFixed(1)} miles away`
  return `${Math.round(mi).toLocaleString()} miles away`
}

export function useLocation() {
  // Kick off on first use (non-blocking)
  if (!initPromise) initLocation()
  return {
    userLat: readonly(userLat),
    userLng: readonly(userLng),
    locationReady: readonly(locationReady),
    locationSource: readonly(locationSource),
    getDistance,
    initLocation,
  }
}
